/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
  useState, useRef, useEffect, useLayoutEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';

import Drawer from '../../juristec-ui/core/Drawer';
import TextArea from '../../juristec-ui/core/TextArea';
import Button from '../../juristec-ui/core/Button';
import Avatar from '../../juristec-ui/core/Avatar';
import MiniLoading from '../../juristec-ui/core/MiniLoading';
import Tooltip from '../../juristec-ui/core/Tooltip';
import IconButton from '../../juristec-ui/core/IconButton';
import Popover from '../../juristec-ui/core/Popover';
import InputTextLine from '../../juristec-ui/core/InputTextLine';
import List from '../../juristec-ui/core/List';
import ListItem from '../../juristec-ui/core/ListItem';
import AssistentTokensBadge from '../AssistentTokensBadge/AssistentTokensBadge';

import assistantAvatar from '../../assets/temp.png';

import {
  HeaderContainer,
  TimelineContainer,
  InputContainer,
  IconWrapper,
  MsgRow,
  MsgContainer,
  LoadingWrapper,
  InputControlsContainer,
  ListIconWrapper,
  FullTimelineContainer,
  GPTCredits,
} from './styled/VirtualAssistant.styled';

import {
  Send,
  HashTag,
  Letters,
  Number,
  DateIcon,
} from '../../juristec-ui/icons';

import { formatDateTime } from '../../juristec-ui/utils/functions/lab';
import { trimString } from '../../juristec-ui/utils/functions/formatString';
import uuidv4 from '../../juristec-ui/utils/functions/randomUUID';

const icons = {
  category: <Letters />,
  object: <Letters />,
  float64: <Number />,
  int64: <Number />,
  'datetime64[ns]': <DateIcon />,
};

const virtualAssistantDefaultPropMessagesState = [null, () => {}];
const virtualAssistantDefaultPropColumns = {};

const VirtualAssistant = ({
  messagesState = virtualAssistantDefaultPropMessagesState,
  submitQuestion,
  loadMoreLogs,
  columns = virtualAssistantDefaultPropColumns,
  isOpen = false,
  close,
  gptTokens,
  blockGeneration,
}) => {
  const cron = useRef();
  const inputRef = useRef();
  const specialChar = useRef('');
  const timelineRef = useRef();

  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [popControl, setPopControl] = useState(false);
  const [inputFilter, setInputFilter] = useState('');
  const [fCols, setFCols] = useState(columns);
  const [messages, setMessages] = messagesState;

  const theme = useTheme();

  useEffect(() => {
    setFCols(columns);
  }, [columns]);

  const closePop = () => {
    setPopControl(false);
    setInputFilter('');
    setFCols(columns);
    specialChar.current = '';
  };

  const scrollToBottom = () => {
    if (timelineRef?.current) {
      timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
    }
  };

  const handleLogsScroll = async (e) => {
    if (e.target.scrollTop <= 0 && timelineRef.current.children.length > 1 && !logsLoading) {
      setLogsLoading(true);
      const beforeMoreScroll = timelineRef.current.scrollHeight;
      await loadMoreLogs();
      timelineRef.current.scrollTop = timelineRef.current.scrollHeight - beforeMoreScroll;
      setLogsLoading(false);
    }
  };

  useLayoutEffect(() => {
    scrollToBottom();
    if (messages) {
      scrollToBottom();
    }
  }, [loading, messages]);

  const controlQuestionVal = (val) => {
    if (val.length <= 200) {
      setQuestion(val);
      return true;
    }
    return false;
  };

  const handleQuestionInput = (e) => {
    const val = e.target.value;
    controlQuestionVal(val);
  };

  const genMemory = () => {
    const lastTenPairs = [];
    let count = 0;
    // Its not functional but is efficient
    for (let i = messages.length - 2; i >= 0; i -= 2) {
      if (messages[i].type === 'question' && messages[i + 1]?.type !== 'error') {
        lastTenPairs.unshift(
          { message: messages[i].message, is_user: true },
          {
            message: messages[i + 1].type === 'text' ? messages[i + 1].message : 'Check it out: <dataframe>',
            is_user: false,
          },
        );
        count += 1;
        if (count === 5) break;
      }
    }
    return lastTenPairs;
  };

  const handleSubmit = async () => {
    closePop();
    const tempQuestion = question.trim();
    const memory = process.env.REACT_APP_FIREBASE_PROJECT_LABEL === 'legalone-analytics' ? null : genMemory();
    setQuestion('');
    setMessages((old) => [
      ...old,
      {
        id: uuidv4(),
        type: 'question',
        message: tempQuestion,
        timestamp: new Date(),
      },
    ]);
    setLoading(true);
    const msg = await submitQuestion(tempQuestion, memory);
    if (msg.type === 'error') setQuestion(tempQuestion);
    setMessages((old) => [
      ...old,
      msg,
    ]);
    setLoading(false);
  };

  const handleInputFilter = (e) => {
    const val = e.target.value;
    setInputFilter(val);
    clearTimeout(cron.current);

    cron.current = setTimeout(() => {
      setFCols(Object.keys(columns).filter((name) => (
        name.toLowerCase().includes(val.toLowerCase())
      )).reduce((aux, key) => {
        aux[key] = columns[key];
        return aux;
      }, {}));
    }, 200);
  };

  const toggleColMentionPop = (e, open) => {
    e.stopPropagation();
    setPopControl((o) => open || !o);
  };

  const addColToInput = (colName) => {
    inputRef.current.focus();
    const cursor = inputRef.current.selectionStart;
    const inputValue = inputRef.current.value;
    if (controlQuestionVal(
      trimString(`${inputValue.slice(0, cursor - specialChar.current.length)}${colName}${inputValue.slice(cursor)}`),
    )) {
      setTimeout(() => {
        inputRef.current.selectionStart = cursor + colName.length;
        inputRef.current.selectionEnd = cursor + colName.length;
      }, 0);
    }
    specialChar.current = '';
  };

  const handleEnterKey = (e) => {
    switch (e.key) {
      case 'Enter': {
        e.preventDefault();
        e.stopPropagation();
        if (!!question.trim() && !loading) {
          handleSubmit();
        }
        break;
      }
      case '#':
        toggleColMentionPop(e, true);
        specialChar.current = '#';
        break;
      default:
        closePop();
        break;
    }
  };

  return (
    <Drawer
      open={isOpen}
      direction="left"
      handleCloseDrawer={close}
      customWidth="25rem"
    >
      <HeaderContainer>
        <h2>Assistente Virtual</h2>
      </HeaderContainer>
      <TimelineContainer ref={timelineRef} onScroll={handleLogsScroll}>
        {(messages === null || logsLoading) && (
          <FullTimelineContainer style={{
            position: 'fixed',
            zIndex: '1',
            backgroundColor: theme.fadedBackground,
            height: 'calc(100vh - 165px)',
          }}
          >
            <MiniLoading fill="primary" />
          </FullTimelineContainer>
        )}
        {messages?.length > 0 ? messages?.map((m) => (
          <MsgRow key={m.id} isUser={m.type === 'question'}>
            {m.type !== 'question' && (
              <Avatar
                style={{ pointerEvents: 'none' }}
                size="large"
                name="Assistente"
                src={assistantAvatar || null}
              />
            )}
            <MsgContainer msgType={m.type}>
              <p>{m.message}</p>
              <span>{formatDateTime(m.timestamp, { time: 'half' })}</span>
            </MsgContainer>
          </MsgRow>
        )) : (
          <FullTimelineContainer>
            <span>
              Nenhuma conversa foi iniciada ainda
              <br />
              Seja o primeiro a começar
            </span>
          </FullTimelineContainer>
        )}
        {loading && (
          <MsgRow>
            <Avatar
              style={{ pointerEvents: 'none' }}
              size="large"
              name="Assistente"
              src={assistantAvatar || null}
            />
            <MsgContainer isLoading>
              <LoadingWrapper>
                <MiniLoading fill="primary" size="small" />
              </LoadingWrapper>
            </MsgContainer>
          </MsgRow>
        )}
        {/* <div ref={timelineEndRef} /> */}
      </TimelineContainer>
      <Popover
        open={popControl}
        closePopover={closePop}
        direction="top-start"
        offset={[16, 2]}
        style={{
          backgroundColor: theme.popoverBackground,
        }}
      >
        <Popover.Action>
          <InputContainer>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              marginBottom: '.2rem',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
            >
              <span style={{ marginRight: 'auto', fontSize: '12px' }}>
                *1 token por mensagem válida
              </span>
              <AssistentTokensBadge
                gptTokens={gptTokens}
              />
            </div>
            <TextArea
              ref={inputRef}
              value={question}
              onChange={handleQuestionInput}
              onKeyDown={handleEnterKey}
              onClick={closePop}
              disabled={loading || blockGeneration}
            />
            <InputControlsContainer>
              <Tooltip text="Colunas" atModal>
                <IconButton
                  onClick={toggleColMentionPop}
                  color={popControl ? 'secondary' : 'primary'}
                  style={{ boxShadow: 'none', padding: '5px' }}
                >
                  <HashTag />
                </IconButton>
              </Tooltip>
              <GPTCredits style={{ display: 'block' }}>
                <span>
                  Alimentado pelo
                  {' '}
                </span>
                <a
                  href="https://openai.com/pt-BR/index/gpt-4o-mini-advancing-cost-efficient-intelligence/"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  GPT-4o mini
                </a>
              </GPTCredits>
              <Tooltip
                textWhen={blockGeneration}
                text="O administrador desativou o uso deste agente para sua conta"
                className="gen-button-tooltip"
                direction="top"
              >
                <Button
                  onClick={handleSubmit}
                  disabled={
                    !question.trim()
                    || loading
                    || (gptTokens.used >= gptTokens.limit && gptTokens.bonus <= 0)
                    || blockGeneration
                  }
                  style={{ boxShadow: 'none', minWidth: 'auto' }}
                >
                  Enviar
                  <IconWrapper>
                    <Send />
                  </IconWrapper>
                </Button>
              </Tooltip>
            </InputControlsContainer>
          </InputContainer>
        </Popover.Action>
        <Popover.Content style={{ backgroundColor: 'white', boxShadow: '0px -1px 4px #9a9a9a' }}>
          <div onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '0 5px 5px 5px' }}>
              <InputTextLine
                placeholder="Buscar coluna..."
                value={inputFilter}
                onChange={handleInputFilter}
                style={{ fontSize: '14px' }}
              />
            </div>
            <List style={{
              maxHeight: '200px',
              overflow: 'hidden auto',
              scrollbarWidth: 'thin',
              borderRadius: '0 0 8px 8px',
            }}
            >
              {Object.keys(fCols).map((name) => (
                <ListItem
                  key={`mention-${name}`}
                  noOutline
                  style={{ fontSize: '16px', padding: '8px 10px' }}
                  onClick={() => addColToInput(name)}
                >
                  <ListIconWrapper typeForColor={fCols[name]}>
                    {icons[fCols[name]]}
                  </ListIconWrapper>
                  {name}
                </ListItem>
              ))}
            </List>
          </div>
        </Popover.Content>
      </Popover>
    </Drawer>
  );
};

VirtualAssistant.propTypes = {
  messagesState: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.string,
      msg: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    })),
    PropTypes.func,
  ])),
  submitQuestion: PropTypes.func.isRequired,
  columns: PropTypes.objectOf(PropTypes.string),
  isOpen: PropTypes.bool,
  close: PropTypes.func.isRequired,
  loadMoreLogs: PropTypes.func.isRequired,
  gptTokens: PropTypes.shape({
    used: PropTypes.number,
    limit: PropTypes.number,
    bonus: PropTypes.number,
  }).isRequired,
  blockGeneration: PropTypes.bool,
};

export default VirtualAssistant;
